import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { GripVertical, Trash2 } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import PageHeader from "../../../components/PageHeader";
import Modal from "../../../components/Modal";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import {
  controleProducaoService,
  ItemProducao,
  SetorProducao,
  UndMedidaOption,
} from "../../../services/controleProducaoService";
import UndMedidaSelect from "../../../components/ControleProducao/UndMedidaSelect";
import { lojaService } from "../../../services/lojaService";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser, hasFullAccessUser } from "../../../lib/screenAccess";
import { Loja } from "../../../types/loja";
import { COLORS } from "../../../constants";

type FormState = {
  id: number | null;
  descricao: string;
  pesoPadrao: string;
  undMedidaId: number | "";
  undMedidaIdBase: number | "";
  ativo: boolean;
  lojaId?: number;
  aplicarEmTodasLojas?: boolean;
  setorIds: number[];
  setorPercentuais: Record<number, number>;
  origemProducao: "LOCAL" | "OUTRA_UNIDADE";
  naoUsaTimer: boolean;
};

const EMPTY_FORM: FormState = {
  id: null,
  descricao: "",
  pesoPadrao: "",
  undMedidaId: "",
  undMedidaIdBase: "",
  ativo: true,
  lojaId: undefined,
  aplicarEmTodasLojas: false,
  setorIds: [],
  setorPercentuais: {},
  origemProducao: "LOCAL",
  naoUsaTimer: false,
};

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

const ControleProducaoItensPage: React.FC = () => {
  const [items, setItems] = useState<ItemProducao[]>([]);
  const [unds, setUnds] = useState<UndMedidaOption[]>([]);
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [setores, setSetores] = useState<SetorProducao[]>([]);

  const loadItems = async (lojaId?: number) => {
    setLoading(true);
    try {
      const data = await controleProducaoService.listItems(lojaId);
      setUnds(data.unds);
      setItems(data.itens);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar itens de produção.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems(selectedLojaId);
  }, [selectedLojaId]);

  useEffect(() => {
    if (!selectedLojaId) {
      setSetores([]);
      return;
    }
    void controleProducaoService
      .listSetores(selectedLojaId)
      .then(setSetores)
      .catch(() => setSetores([]));
  }, [selectedLojaId]);

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  const modalTitle = useMemo(
    () => (form.id ? "Editar Item de Produção" : "Novo Item de Produção"),
    [form.id]
  );
  const lojaOptions = useMemo(
    () =>
      podeVerTodasLojas
        ? lojas
        : lojas.filter((loja) => loja.id === user?.lojaId),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  const persistirOrdem = async (nextItems: ItemProducao[]) => {
    setReordering(true);
    try {
      await controleProducaoService.reorderItems({
        itemIds: nextItems.map((item) => item.id),
        lojaId: selectedLojaId,
      });
      setItems(nextItems);
      toast.success("Ordem dos itens atualizada.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao reordenar itens.");
      await loadItems(selectedLojaId);
    } finally {
      setReordering(false);
    }
  };

  const moverItem = (fromId: number, toId: number) => {
    if (fromId === toId || reordering) return;
    const fromIndex = items.findIndex((item) => item.id === fromId);
    const toIndex = items.findIndex((item) => item.id === toId);
    if (fromIndex < 0 || toIndex < 0) return;

    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    void persistirOrdem(next);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim() || !form.pesoPadrao.trim()) {
      toast.warning("Preencha descrição e peso padrão.");
      return;
    }
    if (form.undMedidaId === "" || form.undMedidaId < 1) {
      toast.warning("Selecione a unidade de medida do peso padrão.");
      return;
    }
    if (form.undMedidaIdBase === "" || form.undMedidaIdBase < 1) {
      toast.warning("Selecione a unidade de medida da base e ficha.");
      return;
    }
    if (form.setorIds.length > 1) {
      const soma = form.setorIds.reduce(
        (acc, id) => acc + (form.setorPercentuais[id] ?? 0),
        0,
      );
      if (Math.abs(soma - 100) > 0.01) {
        toast.warning(`A soma dos percentuais deve ser 100%. Atual: ${soma}%`);
        return;
      }
    }
    setSaving(true);
    try {
      if (form.id) {
        await controleProducaoService.updateItem(form.id, {
          descricao: form.descricao,
          pesoPadrao: form.pesoPadrao,
          undMedidaId: form.undMedidaId,
          undMedidaIdBase: form.undMedidaIdBase,
          ativo: form.ativo,
          lojaId: selectedLojaId,
          setorIds: form.setorIds,
          setorPercentuais: form.setorIds.length > 1 ? form.setorPercentuais : undefined,
          origemProducao: form.origemProducao,
          naoUsaTimer: form.naoUsaTimer,
        });
        toast.success("Item atualizado com sucesso.");
      } else {
        await controleProducaoService.createItem({
          descricao: form.descricao,
          pesoPadrao: form.pesoPadrao,
          undMedidaId: form.undMedidaId,
          undMedidaIdBase: form.undMedidaIdBase,
          ativo: form.ativo,
          lojaId: selectedLojaId,
          aplicarEmTodasLojas: isAdmin && form.aplicarEmTodasLojas,
          setorIds: form.setorIds,
          setorPercentuais: form.setorIds.length > 1 ? form.setorPercentuais : undefined,
          origemProducao: form.origemProducao,
          naoUsaTimer: form.naoUsaTimer,
        });
        toast.success("Item criado com sucesso.");
      }
      setModalOpen(false);
      setForm(EMPTY_FORM);
      await loadItems(selectedLojaId);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar item.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: ItemProducao) => {
    if (!item.podeExcluir) return;
    const result = await Swal.fire({
      title: "Excluir item?",
      text: `Remover "${item.descricao}"? Esta ação não pode ser desfeita.`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: COLORS.PRIMARY,
      confirmButtonText: "Sim, excluir",
    });
    if (!result.isConfirmed) return;
    try {
      await controleProducaoService.deleteItem(item.id, selectedLojaId);
      toast.success("Item excluído com sucesso.");
      await loadItems(selectedLojaId);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir item.");
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <PageHeader
          title="Itens de Produção"
          description="Cadastro de itens por unidade. Arraste as linhas para definir a ordem nas fichas."
        />
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <ReactSelect<LojaOpt, false>
            inputId="itens-loja-select"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            className="w-full min-w-[220px] sm:w-60"
            styles={LOJA_SELECT_STYLES}
          />
          <button
            type="button"
            className="h-[38px] w-full rounded-md bg-primary px-4 text-sm font-semibold text-white sm:w-auto"
            onClick={() => {
              setForm({
                ...EMPTY_FORM,
                lojaId: selectedLojaId,
                aplicarEmTodasLojas: false,
              });
              setModalOpen(true);
            }}
          >
            Novo Item
          </button>
        </div>
      </div>

      {reordering && (
        <p className="mb-3 text-sm text-neutral-500">Salvando nova ordem…</p>
      )}

      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="w-10 px-2 py-3 text-center font-semibold text-neutral-600">
                Ordem
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Descrição
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Peso Padrão
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                UND Peso
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                UND Base
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Setores
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Percentuais
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Origem
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                Status
              </th>
              <th className="px-4 py-3 text-right font-semibold text-neutral-600">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                draggable={!reordering}
                onDragStart={() => setDraggingId(item.id)}
                onDragEnd={() => setDraggingId(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (draggingId != null) {
                    moverItem(draggingId, item.id);
                  }
                  setDraggingId(null);
                }}
                className={`border-t border-neutral-200 transition-colors ${
                  draggingId === item.id
                    ? "bg-primary/5 opacity-60"
                    : "hover:bg-neutral-50"
                }`}
              >
                <td className="px-2 py-3 text-center">
                  <button
                    type="button"
                    draggable={!reordering}
                    onDragStart={() => setDraggingId(item.id)}
                    onDragEnd={() => setDraggingId(null)}
                    className="inline-flex cursor-grab items-center justify-center rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 active:cursor-grabbing"
                    title="Arrastar para reordenar"
                    aria-label={`Reordenar ${item.descricao}`}
                  >
                    <GripVertical size={18} />
                  </button>
                </td>
                <td className="px-4 py-3">{item.descricao}</td>
                <td className="px-4 py-3">{item.pesoPadrao}</td>
                <td className="px-4 py-3">{item.undMedidaNome || "—"}</td>
                <td className="px-4 py-3">{item.undMedidaBaseNome || "—"}</td>
                <td className="px-4 py-3">
                  {item.setores?.length
                    ? item.setores.map((s) => s.nome).join(", ")
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  {item.setores?.length
                    ? item.setores
                        .map((s) => {
                          const percentual = item.setorPercentuais?.[s.id];
                          return `${s.nome}: ${percentual ?? (item.setores?.length === 1 ? 100 : 0)}%`;
                        })
                        .join(" | ")
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  {item.origemProducao === "OUTRA_UNIDADE"
                    ? "Produzido em Birigui"
                    : "Produção local"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      item.ativo
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-neutral-200 text-neutral-700"
                    }`}
                  >
                    {item.ativo ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-700"
                      onClick={() => {
                        setForm({
                          id: item.id,
                          descricao: item.descricao,
                          pesoPadrao: item.pesoPadrao,
                          undMedidaId: item.undMedidaId ?? "",
                          undMedidaIdBase: item.undMedidaIdBase ?? "",
                          ativo: item.ativo,
                          setorIds: item.setorIds ?? [],
                          setorPercentuais: item.setorPercentuais ?? {},
                          origemProducao:
                            item.origemProducao === "OUTRA_UNIDADE"
                              ? "OUTRA_UNIDADE"
                              : "LOCAL",
                          naoUsaTimer: item.naoUsaTimer === true,
                        });
                        setModalOpen(true);
                      }}
                    >
                      Editar
                    </button>
                    {item.podeExcluir && (
                      <button
                        type="button"
                        title="Excluir item"
                        aria-label={`Excluir ${item.descricao}`}
                        className="rounded-md border border-red-200 p-1.5 text-red-600 transition-colors hover:bg-red-50"
                        onClick={() => void handleDelete(item)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-neutral-500"
                  colSpan={10}
                >
                  Nenhum item cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <p className="mt-3 text-sm text-neutral-500">Carregando itens...</p>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
      >
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block text-sm font-medium text-neutral-700">
            Descrição
            <input
              value={form.descricao}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, descricao: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Peso padrão
            <input
              value={form.pesoPadrao}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, pesoPadrao: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            UND do peso padrão
            <div className="mt-1">
              <UndMedidaSelect
                unds={unds}
                value={form.undMedidaId}
                onChange={(undMedidaId) =>
                  setForm((prev) => ({ ...prev, undMedidaId }))
                }
                className="w-full"
              />
            </div>
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            UND da base e ficha
            <div className="mt-1">
              <UndMedidaSelect
                unds={unds}
                value={form.undMedidaIdBase}
                onChange={(undMedidaIdBase) =>
                  setForm((prev) => ({ ...prev, undMedidaIdBase }))
                }
                className="w-full"
              />
            </div>
            <span className="mt-1 block text-xs text-neutral-500">
              Pré-selecionada ao cadastrar bases semanais e fichas diárias.
            </span>
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-neutral-700">
              Setores
            </legend>
            {setores.length === 0 ? (
              <p className="text-xs text-neutral-500">
                Cadastre setores nesta loja para vincular o item.
              </p>
            ) : (
              setores.map((setor) => (
                <div
                  key={setor.id}
                  className="rounded-md border border-neutral-200 px-3 py-2"
                >
                  <label className="flex items-center gap-2 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      checked={form.setorIds.includes(setor.id)}
                      onChange={(e) =>
                        setForm((prev) => {
                          const newIds = e.target.checked
                            ? [...prev.setorIds, setor.id]
                            : prev.setorIds.filter((id) => id !== setor.id);
                          const newPerc = { ...prev.setorPercentuais };
                          if (e.target.checked) {
                            newPerc[setor.id] = newPerc[setor.id] ?? 0;
                          } else {
                            delete newPerc[setor.id];
                          }
                          if (newIds.length === 1) {
                            newPerc[newIds[0]] = 100;
                          }
                          return { ...prev, setorIds: newIds, setorPercentuais: newPerc };
                        })
                      }
                    />
                    {setor.nome}
                  </label>
                  {form.setorIds.includes(setor.id) && (
                    <div className="mt-2 ml-6">
                      <label className="block text-xs font-medium text-neutral-600">
                        Percentual do setor
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="mt-1 w-28 rounded border border-neutral-300 px-2 py-1 text-sm"
                        placeholder="%"
                        value={form.setorPercentuais[setor.id] ?? ""}
                        disabled={form.setorIds.length === 1}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            setorPercentuais: {
                              ...prev.setorPercentuais,
                              [setor.id]: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                      />
                    </div>
                  )}
                </div>
              ))
            )}
            {form.setorIds.length > 0 && (
              <p className={`text-xs font-medium ${
                Math.abs(form.setorIds.reduce((s, id) => s + (form.setorPercentuais[id] ?? 0), 0) - 100) < 0.01
                  ? "text-green-600"
                  : "text-red-600"
              }`}>
                Total: {form.setorIds.reduce((s, id) => s + (form.setorPercentuais[id] ?? 0), 0)}%
                {Math.abs(form.setorIds.reduce((s, id) => s + (form.setorPercentuais[id] ?? 0), 0) - 100) >= 0.01 && " (deve ser 100%)"}
              </p>
            )}
          </fieldset>
          {lojas.length > 1 && selectedLojaId && lojas.find(l => l.id !== selectedLojaId) && (
          <label className="block text-sm font-medium text-neutral-700">
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.origemProducao === "OUTRA_UNIDADE"}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    origemProducao: e.target.checked
                      ? "OUTRA_UNIDADE"
                      : "LOCAL",
                  }))
                }
                className="h-4 w-4 rounded border-neutral-300 text-red-700 focus:ring-red-600"
              />
              Produzido em {lojas.find(l => l.id !== selectedLojaId)?.nome ?? "outra unidade"}
            </span>
            <p className="mt-0.5 text-xs text-neutral-500">
              Marque se este item é produzido por outra unidade (não pela loja atual). O setor será vinculado à unidade produtora.
            </p>
          </label>
          )}
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={form.ativo}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, ativo: e.target.checked }))
              }
            />
            Item ativo
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.naoUsaTimer}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    naoUsaTimer: e.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-neutral-300 text-red-700 focus:ring-red-600"
              />
              Não usa cronômetro
            </span>
            <p className="mt-0.5 text-xs text-neutral-500">
              Marque para itens da ficha diária que não precisam de timer.
            </p>
          </label>
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={Boolean(form.aplicarEmTodasLojas)}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    aplicarEmTodasLojas: e.target.checked,
                  }))
                }
              />
              Cadastrar nas duas lojas
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </Modal>
    </main>
  );
};

export default ControleProducaoItensPage;
